import React from 'react'
import { Box, Button, IconButton, Slide } from '@mui/material'
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { AGE, C, tnum } from './tokens'
import { canAssign, canMoveTo } from './model'
import AssignDriverMenu from './AssignDriverMenu'
import { useDispatchUi } from './context'

const actionSx = {
  height: 32, px: 1.25, flexShrink: 0, textTransform: 'none', fontSize: 13, fontWeight: 500, color: C.text, borderRadius: '8px',
  '&:hover': { bgcolor: C.mutedBg }, '&.Mui-disabled': { color: C.faint },
  '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } }, '& .MuiButton-startIcon svg': { fontSize: 18 },
}

/** Only shown when an action covers part of the selection. */
function Count({ n, of }) {
  if (n === of || n === 0) return null
  return <Box component="span" sx={{ ml: 0.75, px: 0.5, borderRadius: '4px', bgcolor: C.mutedBg, fontSize: 11, color: C.muted, ...tnum }}>{n}</Box>
}

const Label = ({ children }) => <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{children}</Box>

/** Appears once rows are checked; each action says how many of the selection it applies to. */
export default function BulkActionBar({ selected, visibleCount, onSelectAllVisible, onClear, onExport }) {
  const ui = useDispatchUi()
  const n = selected.length
  const assignable = selected.filter(canAssign)
  const preparable = selected.filter(v => canMoveTo(v, 'PREPARED'))
  const cancellable = selected.filter(v => canMoveTo(v, 'CANCELLED'))

  return (
    <Slide direction="up" in={n > 0} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1240, display: 'flex', justifyContent: 'center',
          px: 1.5, pb: 'max(16px, env(safe-area-inset-bottom))', pl: { sm: '252px' }, pointerEvents: 'none',
        }}
      >
        <Box
          role="toolbar"
          aria-label="Bulk actions"
          sx={{
            pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', overflowX: 'auto', p: 0.75,
            bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: '12px',
            boxShadow: '0 12px 32px -8px rgba(16,24,40,0.25), 0 2px 6px rgba(16,24,40,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, flexShrink: 0 }}>
            <Box component="span" sx={{ minWidth: 20, height: 20, px: 0.75, borderRadius: 999, bgcolor: C.brandRed, color: '#fff', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...tnum }}>
              {n}
            </Box>
            <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: C.text, whiteSpace: 'nowrap' }}>selected</Box>
            {n < visibleCount && (
              <Box
                component="button"
                type="button"
                onClick={onSelectAllVisible}
                sx={{ display: { xs: 'none', sm: 'inline' }, border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', font: 'inherit', fontSize: 12, color: C.muted, whiteSpace: 'nowrap', '&:hover': { color: C.text, textDecoration: 'underline' } }}
              >
                Select all {visibleCount}
              </Box>
            )}
          </Box>
          <Box sx={{ width: '1px', height: 20, bgcolor: C.border, mx: 0.5, flexShrink: 0 }} />

          <AssignDriverMenu
            views={assignable}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            {open => (
              <Button onClick={open} disabled={assignable.length === 0} startIcon={<PersonAddAlt1OutlinedIcon />} sx={actionSx}>
                <Label>Assign driver</Label>
                <Count n={assignable.length} of={n} />
              </Button>
            )}
          </AssignDriverMenu>

          <Button disabled={preparable.length === 0} onClick={() => ui.setStatus(preparable, 'PREPARED')} startIcon={<DoneAllIcon />} sx={actionSx}>
            <Label>Mark prepared</Label>
            <Count n={preparable.length} of={n} />
          </Button>

          <Button onClick={onExport} startIcon={<FileDownloadOutlinedIcon />} sx={actionSx}>
            <Label>Export CSV</Label>
          </Button>

          <Button
            disabled={cancellable.length === 0}
            onClick={() => ui.requestCancel(cancellable)}
            startIcon={<CancelOutlinedIcon />}
            sx={{ ...actionSx, color: AGE.late.ink, '&:hover': { bgcolor: AGE.late.bg } }}
          >
            <Label>Cancel</Label>
            <Count n={cancellable.length} of={n} />
          </Button>

          <Box sx={{ width: '1px', height: 20, bgcolor: C.border, mx: 0.5, flexShrink: 0 }} />
          <IconButton size="small" onClick={onClear} aria-label="Clear selection" sx={{ color: C.muted, flexShrink: 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Slide>
  )
}
